// Bilingual artist CV content, consolidated from src/assets/cv source documents.
// Used by both the About page and the printable /cv page so both stay in sync.

const artistCV = {
  en: {
    heading: "About Me",
    name: "Lee Soon Hee (SooNee)",
    contactInfo: "Feel free to reach out at <u>artistsoon@gmail.com</u>.",
    contactButtonLabel: "Say Hey!",
    downloadLabel: "Download CV",
    printButtonLabel: "Download PDF",
    languageToggleLabel: "KR",
    bioParagraphs: [
      "Born on December 27, 1953, South Korean artist Lee Soon Hee, also known as SooNee, began her artistic journey in earnest in 2006. Without formal fine arts training, she has carved a distinctive path in the contemporary art scene, driven by an innate passion for expression and a relentless pursuit of artistic excellence.",
      "Her early works were primarily oil and acrylic on canvas, but as her art evolved, she found a unique voice through the use of coffee and traditional Korean ink on Hanji (Korean mulberry paper). This shift allowed her to blend modern sensibilities with the timeless beauty of Korean heritage, making her work deeply personal yet universally resonant.",
      "Her artistic style is characterized by ethereal, introspective figures that often appear lost in thought, suspended between reality and dream. Over the years, her work has transitioned from delicate floral dreamscapes, such as her *Dream Series*, known for its intricate rose patterns, to the profoundly evocative *Her Series*, where minimalism and quiet emotion take center stage. The figures in her paintings exude solitude, contemplation, and depth, accentuated by the delicate balance of empty space and soft textures.",
      "Lee has held 31 solo exhibitions and participated in over 300 group shows worldwide. Her accolades include the AIAM Prix du Public (2019, France) and the Prix de la Meilleure Artiste Globale (2022, Brussels). Most recently, she exhibited at the 2026 Salon des Indépendants at the Grand Palais in France, further cementing her presence on the global stage.",
      "One of the most memorable moments in her career came when she first experimented with coffee as a medium. An accidental spill on Hanji led her to discover the organic warmth and depth it could bring to her paintings, and this serendipitous event became a turning point, defining much of her recent work.",
    ],
    careerMarkdown: `## Summary

- Solo Exhibitions: 31
- Group Exhibitions: 300+
- Invited Exhibitions: 27
- Collaborations & Special Invited Exhibitions: 6
- Art Fairs: 20+

## Selected Solo Exhibitions (of 31)

| Date | Venue |
|---|---|
| 2006.09.23 | Goyang International Flower Exposition |
| 2008.11.19 | Sejong Center for the Performing Arts, Art Museum |
| 2009.06.15 | Cheongwon Art Museum |
| 2009.11.10 | Hangaram Art Museum, Seoul Arts Center |
| 2011.05.1 | Sunpoong Gallery |
| 2012.03.22 | Uijeongbu Arts Center (full venue) |
| 2013.01.23 | Gallery Ruben, *Muldeulida* |
| 2013.04.18 | Chungmu Art Hall, *Memory Journey* |
| 2014.04.9 | Gallery IS, *Memory and Desire* |
| 2019.03.18 | Hangaram Art Gallery |
| 2019.10.9 | Gallery Conseil, 3rd Exhibition Hall, *Her* |
| 2021.06.11 | Hyehwa Art Center, Main Exhibition Hall |
| 2022.03.14 | AIAM Gallery, IFA, Brussels, Belgium |
| 2024.01.16 | Gallery Ruben |
| 2024.06.15 | Jeongin Gallery |
| 2024.12.5 | Busan BEXCO Booth Solo Exhibition |

## Selected Invited Exhibitions (of 27)

| Date | Venue |
|---|---|
| 2006 | Café Gallery "Shwilman-han", Jungang Church |
| 2007 | Olive Fragrance Gallery, Gwangseong Church (x2: June, December) |
| 2009 | Cheongwon Art Museum, Invited Artist Exhibition |
| 2011.03.30 | Seoul Museum of Art |
| 2013.01.31 | Cheongwon Art Museum |
| 2014.01.15 | Mapo Lifelong Learning Center Gallery |
| 2017.05.16 | Gallery Michelle |
| 2017.09.13 | Gallery Arisu, *8 Femmes* |
| 2019.07.4 | Geumboseong Art Center |
| 2022.07.2 | Art Continue, Gyodae Atelier |
| 2024.09 | InKo Centre, India |
| 2025.02.22 | Yugyeong Art Museum, Invited Solo Exhibition |
| - | Invited Two-Person Exhibition, *"The Woman I Adore"* |
| - | Approximately 100 others |

## Group Exhibitions

- 2006-present, Korea Creative Art Association exhibitions
- 2012-present, Yongsan Art Association exhibitions
- 2012-present, Cheongsaekhoe exhibitions
- 2018-present, "Good Morning, New Morning" (new-work focused) exhibitions
- Approximately 300 others

## Art Fairs (of 20+)

- 2006.09.23 Goyang Art Fair
- 2012.03.22 Uijeongbu Arts Center
- 2013.06.13 SETEC, Seoul Trade Exhibition & Convention Center
- 2015.03.25 CONTEXT in Seoul, COEX, Gallery Michelle
- 2017.11.3 Gunsan Art Fair, Gallery Arisu
- 2020.08.13 BEXCO, Gallery Mayjune
- 2023.04 Hwarang Art Fair
- 2024.09 InKo Centre, India
- 2024.12 Busan International Art Fair (Busan BEXCO)
- 2024.12 COEX Seoul Art Show
- 2025.05 Jo-Hyung Art Fair

## Collaborations & Special Invited Exhibitions (6)

- SNRD Eyewear × SooNee
- Maps Magazine × NookMag
- Puma × Bape
- Lee Kuan Yew × Singapore 50th Anniversary Celebration, Metro Department Store, Singapore
- ArtSalon × MF Amsterdam Event
- Additional brand and gallery collaborations across Korea and Europe

## International Art Activities

- 2019: *Salon des Indépendants*, Grand Palais Éphémère, Paris, France
- 2019: AIAM Independent Drawing Exhibition, Carrousel du Louvre, Paris, France
- 2022: *Projet de l'Esprit Nouveau à Travels*, AIAM Gallery, IFA, Brussels, Belgium
- 2022: AIAM–ADAGP Global Members Exhibition, Gallery Iaon
- 2023: *Salon des Indépendants*, Grand Palais Éphémère, Paris, France
- 2023: AIAM–ADAGP Global Members Exhibition, Gallery Iaon
- 2024: *Salon des Indépendants*, Grand Palais Éphémère, Paris, France
- 2024: AIAM–ADAGP Global Members Exhibition, Galerie Au Médicis, Paris, France
- 2025: *Salon des Indépendants*, Grand Palais Éphémère, Paris, France
- 2025: AIAM–ADAGP Global Members Exhibition, Agnes Nord Galerie, Paris, France
- 2026: *Salon des Indépendants*, Grand Palais, Paris, France

## Awards

**Domestic (Korea)**
- 2008: MBC Geumgang Art Competition
- 2009: 1st Cheongwon National Art Competition, Grand Prize
- 2009: Korean Art Light Open Call Exhibition
- 2009: 10th Shin Saimdang Art Competition
- 2010: 8th Gyeomjae Jingyeong Art Competition
- 2010: MBC Geumgang Art Competition
- 2010: 7th Yongsan International Art Competition, Special Prize
- 2011: 9th Gyeomjae Jingyeong Art Competition
- 2011: MBC Geumgang Art Competition
- 2011: Gyeonggi-do Peace Art Competition
- 2012: Korea Land & Maritime International Open Call
- 2012: Korea Contemporary Formative Art Competition

**International**
- 2019.02: Prix du Public (Public Choice Award), Salon des Indépendants, Grand Palais Éphémère, Paris, France
- 2022.02: Prix de la Meilleure Artiste Globale (Best Global Artist Award)
- 2024.11: Prix du Public (Public Choice Award)
- 2025.03: Prix d'Excellence (Excellence Award), LE SALON DES INDÉPENDANTS, France

## Permanent Collection

- **2024.03**: *Attestation d'une Collection Permanente* — Five works (including a work alongside one by So Ji-sub) acquired for the permanent collection of AIAM Gallery, IFA, Brussels, Belgium.

## Recognition & Membership

- Full Member, Anthromeda
- Lifetime Member, ADAGP
- Lifetime Member, Taylor Foundation
- Honorary Full Member, Les Indépendants
- Listed as an artist in Art Fabetic and the Global Directory of Artists, 2021
- AIAM (Association Internationale André Malraux)
- Korea Creative Art Association
- Yongsan Art Association
- Cheongsaekhoe (member)
- Hooks Avenue (Singapore), Artist
`,
  },
  kr: {
    heading: "작가 소개",
    name: "이순희 (수니)",
    contactInfo: "문의사항은 <u>artistsoon@gmail.com</u>으로 편하게 연락주세요.",
    contactButtonLabel: "문의하기",
    downloadLabel: "CV 다운로드",
    printButtonLabel: "PDF 다운로드",
    languageToggleLabel: "EN",
    bioParagraphs: [
      "1953년 12월 27일 출생한 한국의 현대 미술가 이순희(수니로도 알려진)는 2006년부터 본격적으로 작품 활동을 시작했습니다. 정규 미술 교육을 받지 않았음에도 불구하고, 예술에 대한 타고난 열정과 끊임없는 탐구를 바탕으로 독창적인 작품 세계를 구축해왔습니다.",
      "초기 작품은 주로 캔버스에 유화와 아크릴로 표현되었으나, 이후 커피와 한국 전통 먹, 그리고 닥나무로 만든 한지(뽕나무 종이)라는 독특한 매체를 활용하게 되었습니다. 이러한 전환은 현대적 감수성과 한국 전통미를 조화롭게 융합하여, 작품에 깊은 개인적 의미와 보편적 공감을 동시에 담아낼 수 있게 했습니다.",
      "그녀의 예술적 스타일은 몽환적이면서도 사색적인 인물 표현이 특징입니다. 초기에는 정교한 장미 패턴이 돋보이는 《드림 시리즈(Dream Series)》를 통해 꿈속 같은 분위기를 연출했으며, 이후 《Her 시리즈》를 통해 더욱 미니멀하고 감성적인 작품 세계를 선보이고 있습니다. 그녀의 작품 속 인물들은 고요한 사유에 잠긴 듯 여백과 부드러운 질감 속에서 고독과 사색, 깊이를 자아냅니다.",
      "이순희는 지금까지 31회의 개인전을 개최했으며, 전 세계적으로 300여회의 단체전에 참여했습니다. 2019년 프랑스 AIAM 대중인기상(Prix du Public), 2022년 브뤼셀 최우수 글로벌 예술상(Prix de la Meilleure Artiste Globale) 등을 수상하며 국제적으로도 주목받고 있습니다. 가장 최근에는 2026년 프랑스 그랑 팔레 앙데팡당(Salon des Indépendants) 전시를 통해 글로벌 무대에서의 입지를 더욱 확고히 했습니다.",
      "그녀의 예술 여정에서 가장 기억에 남는 순간 중 하나는 커피를 매체로 사용하게 된 계기였습니다. 한지 위에 우연히 커피를 흘린 후, 그 자연스러운 색감과 깊이에 매료되어 이를 작품에 적극적으로 활용하기 시작했습니다. 이 뜻밖의 발견은 그녀의 작업에 중요한 전환점이 되었으며, 이후 대표적인 기법으로 자리 잡았습니다.",
    ],
    careerMarkdown: `## 요약

- 개인전: 31회
- 단체전: 300여회
- 초대전: 27회
- 콜라보레이션 및 특별 초대전: 6회
- 아트페어: 20여회

## 개인전 (선정, 31회 중)

| 일자 | 장소 |
|---|---|
| 2006.09.23 | 고양세계꽃박람회장 |
| 2008.11.19 | 세종문화회관 미술관 |
| 2009.06.15 | 청원 미술관 |
| 2009.11.10 | 예술의전당 한가람 미술관 |
| 2011.05.1 | 순풍갤러리 |
| 2012.03.22 | 의정부 예술의 전당 전관 |
| 2013.01.23 | 갤러리 루벤 《물들이다》 |
| 2013.04.18 | 충무아트홀 《추억여행》 |
| 2014.04.9 | 갤러리 이즈 《기억과 욕망》 |
| 2019.03.18 | 한가람 아트갤러리 |
| 2019.10.9 | 갤러리 콩세유 3관 《Her》 |
| 2021.06.11 | 혜화아트센터 제1전시장 |
| 2022.03.14 | AIAM Gallery IFA, 브뤼셀 미술관 |
| 2024.01.16 | 갤러리 루벤 |
| 2024.06.15 | 정인 갤러리 |
| 2024.12.5 | 부산 벡스코 부스개인전 |

## 초대전 (선정, 27회 중)

| 일자 | 장소 |
|---|---|
| 2006 | 중앙교회 쉴만한 카페갤러리 |
| 2007 | 광성교회 올리브향기 갤러리 (2회: 6월, 12월) |
| 2009 | 청원미술관 초대작가전 |
| 2011.03.30 | 서울미술관 |
| 2013.01.31 | 청원미술관 |
| 2014.01.15 | 마포평생학습관 갤러리 |
| 2017.05.16 | 갤러리 미쉘 |
| 2017.09.13 | 갤러리 아리수 《8 Femmes》 |
| 2019.07.4 | 금보성 아트센터 |
| 2022.07.2 | 아트컨티뉴 교대 아뜰리에 |
| 2024.09 | 인코센터, 인도 |
| 2025.02.22 | 유경미술관 초대개인전 |
| - | 초대2인전 《경애하는 그녀》 |
| - | 그 외 100여회 |

## 단체전

- 2006~현재 대한민국창작미술협회 전
- 2012~현재 용산미술협회 전
- 2012~현재 청색회전
- 2018~현재 신작중심 굿모닝 새아침전
- 그 외 300여회

## 아트페어 (20여회 중)

- 2006.09.23 고양아트페어
- 2012.03.22 의정부 예술의전당
- 2013.06.13 SETEC 서울무역전시 컨벤션센터
- 2015.03.25 CONTEXT IN SEOUL, 코엑스 갤러리 미쉘
- 2017.11.3 군산아트페어, 갤러리 아리수
- 2020.08.13 BEXCO, 갤러리 메이준
- 2023.04 화랑아트페어
- 2024.09 인코센터, 인도
- 2024.12 부산국제아트페어 (부산 벡스코)
- 2024.12 코엑스 서울아트쇼
- 2025.05 조형아트페어

## 콜라보레이션 및 특별 초대전 (6회)

- SNRD Eyewear × SooNee
- Maps Magazine × NookMag
- Puma × Bape
- 리관유(Lee Kuan Yew) × Singapore 50주년 기념일, 싱가포르 메트로 백화점
- ArtSalon × MF Amsterdam Event
- 한국 및 유럽 갤러리·브랜드와의 추가 협업

## 해외 특별전시 참가

- 2019: Salon des Indépendants (Grand Palais Éphémère, Paris, France)
- 2019: AIAM 앙데팡당 드로잉전 (Carrousel du Louvre, Paris, France)
- 2022: Projet de l'Esprit Nouveau à Travels, AIAM Gallery (IFA, 브뤼셀, 벨기에)
- 2022: AIAM–ADAGP 글로벌 연합회원전 (Gallery Iaon)
- 2023: Salon des Indépendants (Grand Palais Éphémère, Paris, France)
- 2023: AIAM–ADAGP 글로벌 연합회원전 (Gallery Iaon)
- 2024: Salon des Indépendants (Grand Palais Éphémère, Paris, France)
- 2024: AIAM–ADAGP 글로벌 연합회원전 (Galerie Au Médicis, Paris, France)
- 2025: Salon des Indépendants (Grand Palais Éphémère, Paris, France)
- 2025: AIAM–ADAGP 글로벌 연합회원전 (Agnes Nord Galerie, Paris, France)
- 2026: Salon des Indépendants (Grand Palais, Paris, France)

## 수상 내역

**국내**
- 2008: MBC 금강 미술대전
- 2009: 제1회 대한민국 청원 미술대전 최우수상
- 2009: 한국미술의 빛 작가공모전
- 2009: 제10회 신사임당 미술대전
- 2010: 제8회 겸재 진경 미술대전
- 2010: MBC 금강 미술대전
- 2010: 제7회 용산국제미술대전 특별상
- 2011: 제9회 겸재 진경 미술대전
- 2011: MBC 금강 미술대전
- 2011: 경기도 평화 미술대전
- 2012: 한국 국토해양 국제공모전
- 2012: 대한민국 현대조형 미술대전

**국제**
- 2019.02: Prix du Public (대중인기상), Salon des Indépendants (Grand Palais Éphémère, Paris, France)
- 2022.02: Prix de la Meilleure Artiste Globale (최우수 글로벌 예술상)
- 2024.11: Prix du Public (대중인기상)
- 2025.03: Prix d'Excellence (우수작가상), LE SALON DES INDÉPENDANTS, France

## 영구 소장

- **2024.03**: *Attestation d'une Collection Permanente* — 작품 5점(소지섭 작품 외)이 벨기에 AIAM Gallery IFA 브뤼셀 미술관에 영구 소장됨.

## 회원 및 등재

- Anthromeda 정회원
- ADAGP 종신회원
- 테일러 재단 종신회원
- 앙데팡당 우대 정회원
- Art Fabetic 사이트 & 글로벌 인명사전 등재작가, 2021
- AIAM 국제 앙드레 말로 협회
- 대한민국 창작미술협회
- 용산미술협회
- 청색회 회원
- Hooks Avenue (Singapore) Artist
`,
  },
};

export default artistCV;
